import bind from 'bind-decorator';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import { ManagerAuthorization } from '@common/enums';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';
import { tryParseInt } from '../../../../common/utils/tryParseInt';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import {
    AdminTable,
    DeleteButton,
    EditButton,
    Header,
    HeaderSearchBox,
    Link,
    LinkButton,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../../elements/common';
import { Component } from '../../elements/Component';
import { ListPager } from '../../elements/ListPager';
import { NullableFormattedDate } from '../../elements/NullableFormattedDate';
import { FullWidthTemplate } from '../../templates/FullWidthTemplate';

type UzoviListProps = IWithRouterProps;

interface IUzovi {
    authorization: {
        canDelete: boolean;
        canUpdate: boolean;
    };
    code: string;
    endDate: string | null;
    id: string;
    insurer?: {
        id: string;
        name: string;
    };
    name: string;
    startDate: string;
}

interface IUzoviListState {
    authorization: {
        canAdd: boolean;
    };
    isSearching: boolean;
    items: IUzovi[];
    searchText: string | null;
    total: number;
}

class InnerUzoviList extends Component<UzoviListProps, IUzoviListState> {
    constructor(props: UzoviListProps) {
        super(props);

        assertHasAuthorization([ManagerAuthorization.uzovisRead, ManagerAuthorization.uzovisAddEdit]);

        this.state = {
            authorization: {
                canAdd: false,
            },
            isSearching: true,
            items: [],
            searchText: this.props.router.searchParams.get('searchText'),
            total: 0,
        };
    }

    public async componentDidMount() {
        await this.search();
    }

    public render() {
        let actionButtons: JSX.Element[] | undefined;
        const page = this.props.router.searchParams.getOptional('page');
        if (this.state.authorization.canAdd) {
            actionButtons = [
                <LinkButton
                    icon="add-button"
                    key="add-button"
                    to="/uzovis/add"
                >
                    {translateKey('uzovi.add')}
                </LinkButton>,
            ];
        }

        return (
            <div>
                <FullWidthTemplate
                    breadcrumb={[
                        { text: translateKey('common.settings') },
                        { text: translateKey('uzovi.uzovis'), url: '/uzovis' },
                    ]}
                >
                    <Header
                        actionButtons={actionButtons}
                        content={translateKey('uzovi.uzovis')}
                        onSearch={async () => await this.handleNavigation(1)}
                        searchFields={[
                            <HeaderSearchBox
                                key="search"
                                onChange={async (value) => await this.setState({ searchText: value })}
                                text={translateKey('uzovi.codeOrName')}
                                value={this.state.searchText}
                            />,
                        ]}
                    />

                    <AdminTable>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{translateKey('uzovi.code')}</TableHeader>
                                <TableHeader>{translateKey('common.name')}</TableHeader>
                                <TableHeader>{translateKey('common.startDate')}</TableHeader>
                                <TableHeader>{translateKey('common.endDate')}</TableHeader>
                                <TableHeader>{translateKey('insurers.latestInsurer')}</TableHeader>
                                <TableHeader />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {this.state.items.map((item) => (
                                <TableRow key={item.id}>
                                    <TableData>{item.code}</TableData>
                                    <TableData>{item.name}</TableData>
                                    <TableData>
                                        <NullableFormattedDate value={item.startDate} />
                                    </TableData>
                                    <TableData>
                                        <NullableFormattedDate value={item.endDate} />
                                    </TableData>
                                    <TableData>
                                        {item.insurer &&
                                            (hasOneAuthorization(ManagerAuthorization.insurersAddEdit) ? (
                                                <Link to={`/insurers/${item.insurer.id}`}>{item.insurer.name}</Link>
                                            ) : (
                                                item.insurer.name
                                            ))}
                                    </TableData>
                                    {item.authorization.canUpdate && (
                                        <TableData align="right">
                                            <EditButton
                                                to={`/uzovis/${item.id}/edit${(page && `?returnPage=${page}`) || ''}`}
                                            />
                                            {item.authorization.canDelete && (
                                                <DeleteButton onClick={() => this.remove(item.id)} />
                                            )}
                                        </TableData>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </AdminTable>
                    <ListPager
                        isDisabled={this.state.isSearching}
                        onChangePage={this.handleNavigation}
                        page={tryParseInt(page, 1)}
                        total={this.state.total}
                    />
                </FullWidthTemplate>
            </div>
        );
    }

    @bind
    private async handleNavigation(page: number) {
        let url = `/uzovis?page=${page}`;
        if (this.state.searchText) {
            url += '&searchText=' + encodeURIComponent(this.state.searchText);
        }
        this.props.router.navigate(url);
        await this.search();
    }

    private async search() {
        await this.setState({ isSearching: true });
        let url = `/api/uzovis?includeLastInsurer=true&includeItemAuthorizations=canUpdate,canDelete&includeListAuthorizations=canAdd&page=${this.props.router.searchParams.get(
            'page'
        )}`;

        if (this.state.searchText) {
            url += '&searchText=' + encodeURIComponent(this.state.searchText);
        }

        const result = await fetchJson(url, 'GET');
        await this.setState({
            items: result.json.items,
            isSearching: false,
            total: result.json.total,
            authorization: result.json.authorization,
        });
    }

    private async remove(id: string) {
        const url = '/api/uzovis/' + id;
        await this.setState({ isSearching: true });
        await fetchJson(url, 'DELETE');
        await this.search();
    }
}

export const UzoviList = withRouter(InnerUzoviList);
